import React, { useMemo } from 'react';
import { map, isNil, isEmpty, fromPairs, find, noop } from 'lodash';
import shortid from 'shortid';
import ReactTooltip from 'react-tooltip';

// local dependencies
import { button as Button } from 'modules/button';
import { Icon } from 'modules/icon-bank';
import { Form } from 'modules/form';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { dispositionNotesWidget as DispositionNotesWidget } from 'ui/views/widgets/dispositionNotesWidget';
 *
 * // render
 * <DispositionNotesWidget propName='propValue'/>
 */

const TruncatedString = ( props ) => {
	const result = [];
	const _string = isEmpty( props.string ) ? '' : props.string;

	if(  108 < _string.length ) {
		result.push(
			<div key={ props.uniqId } className='ui-disposition-notes-widget__readonly__items__item__description__text__detail'>
				{ _string.substring( 0, 105 ) }
				<a
					data-tip
					data-for={ props.uniqId }
					className='ui-disposition-notes-widget__readonly__items__item__description__text__detail__read-more'
				>
					...Read More
				</a>
			</div>
		);
	} else {
		result.push( <div key={ props.uniqId } className='ui-disposition-notes-widget__readonly__items__item__description__text__detail'>{ props.string }</div> );
	}

	return ( <div className='ui-disposition-notes-widget__readonly__items__item__description__text'>{ result } </div> );
};

//  Readonly View
const DispositionNotesReadonly = ( props ) => {

	// extract actions
	const addAction = find( props.actions, { name: 'add' } );

	return (
		<div className='ui-disposition-notes-widget__readonly'>
			{ /* add button */ }
			<div className='ui-disposition-notes-widget__readonly__add-button'>
				<Button
					className='ui-disposition-notes-widget__readonly__add-button__button'
					title={ addAction.title }
					size='extra-small'
					onClick={ () => props.onToggleEditMode( true ) }
				/>
			</div>

			{ /* item */ }
			<div className='ui-disposition-notes-widget__readonly__items'>
				{ map( props.items, ( item, index ) => {

					const uniqId = useMemo( shortid.generate, [] );

					return (
						<div
							key={ item.id }
							className='ui-disposition-notes-widget__readonly__items__item'
						>
							<div className='ui-disposition-notes-widget__readonly__items__item__header'>
								<div className='ui-disposition-notes-widget__readonly__items__item__header__text'>
									{ item.title }
								</div>
								<div className='ui-disposition-notes-widget__readonly__items__item__header__actions'>
									<div
										className='ui-disposition-notes-widget__readonly__items__item__header__actions__edit'
										onClick={ () =>
											props.onToggleEditMode( true, item.id, 'edit' )
										}
									>
										<Icon name='edit' />
									</div>
									<div
										className='ui-disposition-notes-widget__readonly__items__item__header__actions__delete'
										onClick={ () => props.onDeleteClick( { id: item.id } ) }
									>
										<Icon name='delete' />
									</div>
								</div>
							</div>
							<div className='ui-disposition-notes-widget__readonly__items__item__description'>
								<TruncatedString string={ item.description } uniqId={ uniqId } />

								{/* { item.description } */}
							</div>

							{ /* tooltip */ }
							{
								<ReactTooltip
									id={ uniqId }
									aria-haspopup='true'
									delayShow={ 300 }
									className='ui-disposition-notes-widget__readonly__items__item__tooltips'
									backgroundColor={ '#cdd7e1' }
									place="bottom"
								>
									<p
										key={ index }
										className='ui-disposition-notes-widget__readonly__items__item__tooltips__text'
									>
										{ item.description }
									</p>
								</ReactTooltip>
							}
						</div>
                    );
                } )
                }
			</div>
        </div>
	);
};

/**---------------- */

//  Editable or Add View

const DispositionNotesEditable = ( props ) => {

	// extract actions
	const cancelAction = find( props.actions, { name: 'cancel' } );
	const saveAction = find( props.actions, { name: 'save' } );
	const updateAction = find( props.actions, { name: 'update' } );

	// find currently editing item based on `props.editItemId`
	const item = isNil( props.editItemId )
		? {}
		: find( props.items, { id: props.editItemId } );

	// choosing between save or update action
	const actions = [];
	isEmpty( item ) ? actions.push( saveAction ) : actions.push( updateAction );

	return (
		<Form
			fields={ fromPairs( map( props.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
			values={ item } // default values of the item
			onSubmit={ ( { values, isValid } ) => {
				if ( isValid ) {
					props.onFormSubmit( {
						id: props.editItemId,
						title: values.title,
						description: values.description,
					} );
				}
			} }
            className='ui-disposition-notes-widget__editable'
		>
			{ ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {

				return (
					<form onSubmit={ onSumbit }>
						{ /* fields */ }
						{ map( fields, ( field ) => {
							return (
								<field.view
									key={ field.name }
									className='ui-disposition-notes-widget__editable__field'
									name={ field.name }
									required={ field.required }
									placeholder={ field.placeholder }
									label={ field.title }
									onChange={ onChange }
									value={ values[ field.name ] }
									error={ errors[ field.name ] }
									maxLength={ field.maxLength }
								/>
							);
						} ) }

						{ /* actions */ }
						<div className='ui-disposition-notes-widget__editable__buttons'>
							<Button
								className='ui-disposition-notes-widget__editable__buttons__item'
								size='extra-small'
								outline={ true }
								title={ cancelAction.title }
								onClick={ () => props.onToggleEditMode( false ) }
							/>

							{ actions.map( ( action ) => (
								<Button
									className='ui-disposition-notes-widget__editable__buttons__item'
									size='extra-small'
									label={ action.title }
									type='submit'
									disabled={ !isValid }
									key={ action.name }
								/>
							) ) }
						</div>
					</form>
                );
			} }
		</Form>
    );
};

export const dispositionNotesWidget = ( props ) => {

    return (
        <div className='ui-disposition-notes-widget'>
            { 'readonly' === props.mode ? (
				<DispositionNotesReadonly { ...props } />
			) : (
				<DispositionNotesEditable { ...props } />
			) }
        </div>
    );
};

// set display name
dispositionNotesWidget.displayName = 'dispositionNotesWidget';

// set default props
dispositionNotesWidget.defaultProps = {
    actions: [],
    editItemId: null,
    fields: [],
    items: [],
    mode: 'readonly',
    onDeleteClick: noop,
    onFormSubmit: noop,
    onToggleEditMode: noop
};

// prop types
dispositionNotesWidget.propTypes = {};
